import { useEffect, useState, useCallback } from "react";
import { Tag, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Globe, Store, Loader2, RefreshCw } from "lucide-react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import CreateCouponModal from "../../components/modals/CreateCouponModal";

function fmtCOP(n) {
  return Number(n ?? 0).toLocaleString("es-CO");
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
}

function statusBadge(coupon) {
  const expired = coupon.endDate && new Date(coupon.endDate) < new Date();
  const days = daysUntil(coupon.endDate);
  if (!coupon.active) return { label: "Inactivo", cls: "bg-gray-100 text-gray-500" };
  if (expired) return { label: "Vencido", cls: "bg-red-100 text-red-600" };
  if (days !== null && days <= 7) return { label: `Vence en ${days}d`, cls: "bg-amber-100 text-amber-700" };
  return { label: "Activo", cls: "bg-emerald-100 text-emerald-700" };
}

// Panel admin para cupones de cualquier tienda y cupones de plataforma (aplican a
// cualquier tienda, companyId null) — antes solo existía la gestión por tienda en
// CompanyCoupons.jsx, no había forma de crear/editar un cupón que aplicara a todo
// Qkiere a la vez.
export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState("all"); // all | platform | store
  const [openModal, setOpenModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, compRes] = await Promise.all([
        API.get("/coupons"),
        API.get("/companies", { params: { limit: 500 } }),
      ]);
      setCoupons(cRes.data?.data?.coupons ?? []);
      setCompanies(compRes.data?.data?.data ?? []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cargando cupones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCoupon = async (id) => {
    try {
      await API.patch(`/coupons/${id}/toggle`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error cambiando estado");
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`¿Eliminar el cupón "${coupon.code}"? Esta acción no se puede deshacer.`)) return;
    try {
      await API.delete(`/coupons/${coupon.id}`);
      toast.success("Cupón eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error eliminando el cupón");
    }
  };

  const filtered = coupons.filter((c) => {
    if (scopeFilter === "platform") return c.companyId === null;
    if (scopeFilter === "store") return c.companyId !== null;
    return true;
  });

  const platformCount = coupons.filter((c) => c.companyId === null).length;
  const storeCount = coupons.length - platformCount;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 pb-16">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-xl font-black text-gray-900 flex items-center gap-2">
          <Tag size={20} className="text-indigo-600" /> Cupones
        </h1>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 transition">
            <RefreshCw size={15} />
          </button>
          <button
            onClick={() => setOpenModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition shadow-sm"
          >
            <Plus size={15} /> Crear cupón
          </button>
        </div>
      </div>

      {/* Filtro por alcance */}
      <div className="flex gap-1.5 mb-5">
        {[
          { key: "all", label: "Todos", count: coupons.length },
          { key: "platform", label: "De plataforma", count: platformCount, icon: Globe },
          { key: "store", label: "Por tienda", count: storeCount, icon: Store },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setScopeFilter(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              scopeFilter === tab.key ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
            }`}
          >
            {tab.icon && <tab.icon size={11} />}
            {tab.label}
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${scopeFilter === tab.key ? "bg-white/20" : "bg-gray-100 text-gray-500"}`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-indigo-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <Tag size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm font-semibold">Sin cupones en esta categoría</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.map((c) => {
            const badge = statusBadge(c);
            return (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="font-mono font-black text-gray-900 tracking-wider">{c.code}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                </div>
                <p className="text-sm font-bold text-indigo-600 mb-1">
                  {c.discountType === "percentage" ? `${c.discountValue}% de descuento` : `$${fmtCOP(c.discountValue)} de descuento`}
                </p>
                {c.description && <p className="text-xs text-gray-500 mb-2 line-clamp-2">{c.description}</p>}
                <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
                  {c.companyId === null ? (
                    <span className="flex items-center gap-1 text-indigo-600 font-semibold"><Globe size={11} /> Todas las tiendas</span>
                  ) : (
                    <span className="flex items-center gap-1"><Store size={11} /> {c.company?.name ?? "Tienda"}</span>
                  )}
                  <span className="text-gray-300">·</span>
                  <span>{c.usedCount} / {c.maxUses ?? "∞"} usos</span>
                  {c.endDate && (<><span className="text-gray-300">·</span><span>Hasta {new Date(c.endDate).toLocaleDateString("es-CO")}</span></>)}
                </div>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingCoupon(c)} className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition">
                    <Pencil size={12} /> Editar
                  </button>
                  <button onClick={() => deleteCoupon(c)} className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition">
                    <Trash2 size={12} />
                  </button>
                  <button
                    onClick={() => toggleCoupon(c.id)}
                    className={`flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5 rounded-lg border transition ${
                      c.active ? "border-red-200 text-red-600 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                    }`}
                  >
                    {c.active ? <><ToggleRight size={13} /> Desactivar</> : <><ToggleLeft size={13} /> Activar</>}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {openModal && (
        <CreateCouponModal isAdmin companies={companies} onClose={() => setOpenModal(false)} onSaved={load} />
      )}
      {editingCoupon && (
        <CreateCouponModal isAdmin companies={companies} coupon={editingCoupon} onClose={() => setEditingCoupon(null)} onSaved={load} />
      )}
    </div>
  );
}
